import { SortDir, TableColumn } from "./types"

// Client-side, stable-ish sort used by uncontrolled tables (their data is held
// in the browser, so sorting happens here). Numbers sort numerically, booleans
// false→true, everything else by locale.
export const sortRows = <T,>(rows: T[], accessor: (row: T) => unknown, dir: SortDir): T[] => {
  const m = dir === "asc" ? 1 : -1
  return [...rows].sort((a, b) => {
    const av = accessor(a)
    const bv = accessor(b)
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * m
    if (typeof av === "boolean" && typeof bv === "boolean") return (Number(av) - Number(bv)) * m
    return String(av ?? "").localeCompare(String(bv ?? "")) * m
  })
}

// Free-text search across a row's column values. Uses a column's render output
// only when it's a primitive (string/number); when render returns a React
// element (e.g. a name cell wrapped in an <a>) it falls back to the raw row[key]
// so the column stays searchable instead of matching "[object Object]".
export const filterBySearch = <T,>(rows: T[], columns: TableColumn<T>[], term: string): T[] => {
  const needle = term.toLowerCase()
  if (needle === "") return rows
  return rows.filter((row) =>
    columns.some((col) => {
      const rendered = col.render ? col.render(row) : undefined
      const value =
        typeof rendered === "string" || typeof rendered === "number"
          ? rendered
          : (row as Record<string, unknown>)[col.key]
      return String(value ?? "").toLowerCase().includes(needle)
    })
  )
}

// Slice a row set to a single page (1-indexed).
export const paginate = <T,>(rows: T[], page: number, itemsPerPage: number): T[] => {
  const end = page * itemsPerPage
  return rows.slice(end - itemsPerPage, end)
}
